import { 
  Building2, 
  AlertCircle
} from "lucide-react";
import { formatCurrency } from "../../utils/format";

interface MessageContentProps {
  content: string;
  data?: any;
  action?: string;
  isError?: boolean;
}

export function MessageContent({ content, data, action, isError }: MessageContentProps) {
  // If error, just show the message
  if (isError) {
    return (
      <div className="flex flex-col gap-2 text-red-700">
        <div className="flex items-center gap-2 font-medium">
          <AlertCircle className="w-4 h-4" />
          <span>Error</span>
        </div>
        <p className="text-sm whitespace-pre-wrap">{content}</p>
      </div>
    );
  }

  // Determine what to render based on action and data
  const renderData = () => {
    if (!data) return null;

    // READ FUNDS -> List of funds
    if (action === 'read_funds' && Array.isArray(data)) {
      return (
        <div className="mt-3 space-y-2">
          {data.slice(0, 5).map((fund: any) => (
            <div key={fund.id} className="bg-white p-3 rounded-lg border border-neutral-200 shadow-sm text-sm">
              <div className="flex justify-between items-start mb-1">
                <h4 className="font-medium text-neutral-900">{fund.name}</h4>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                  fund.priority === 'tier_1' ? 'bg-purple-100 text-purple-700' :
                  fund.priority === 'tier_2' ? 'bg-blue-100 text-blue-700' :
                  'bg-neutral-100 text-neutral-600'
                }`}>
                  {fund.priority?.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              <div className="flex justify-between items-center text-xs text-neutral-500">
                <span>{fund.stage?.replace('_', ' ')}</span>
                {fund.website && (
                  <a href={fund.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    Visit
                  </a>
                )}
              </div>
            </div>
          ))}
          {data.length > 5 && (
            <div className="text-center text-xs text-neutral-500 pt-1">
              + {data.length - 5} more funds
            </div>
          )}
        </div>
      );
    }

    // READ/CREATE/UPDATE FUND -> Single Fund Card
    if ((action === 'read_fund_detail' || action === 'create_fund' || action === 'update_fund') && data.id) {
      const fund = data;
      return (
        <div className="mt-3 bg-white p-4 rounded-lg border border-neutral-200 shadow-sm">
          <div className="flex items-center gap-2 mb-3 pb-3 border-b border-neutral-100">
            <Building2 className="w-4 h-4 text-neutral-500" />
            <h3 className="font-medium text-neutral-900">{fund.name}</h3>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-neutral-500">Stage</span>
              <span className="font-medium capitalize">{fund.stage?.replace('_', ' ')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-500">Priority</span>
              <span className="font-medium capitalize">{fund.priority?.replace('_', ' ')}</span>
            </div>
            {fund.typicalTicketMin && (
              <div className="flex justify-between">
                <span className="text-neutral-500">Check Size</span>
                <span className="font-medium">
                  {formatCurrency(fund.typicalTicketMin)} - {formatCurrency(fund.typicalTicketMax)}
                </span>
              </div>
            )}
            {fund.thesis && (
              <div className="pt-2 text-xs text-neutral-600 border-t border-neutral-100 mt-2">
                "{fund.thesis}"
              </div>
            )}
          </div>
        </div>
      );
    }

    // PIPELINE SUMMARY -> Stats Grid
    if (action === 'read_pipeline_summary') {
      return (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="bg-white p-3 rounded-lg border border-neutral-200 text-center">
            <div className="text-xs text-neutral-500 mb-1">Total Funds</div>
            <div className="text-lg font-semibold">{data.totalFunds || 0}</div>
          </div>
          <div className="bg-white p-3 rounded-lg border border-neutral-200 text-center">
            <div className="text-xs text-neutral-500 mb-1">Active</div>
            <div className="text-lg font-semibold">{data.activeFunds || 0}</div>
          </div>
          <div className="bg-white p-3 rounded-lg border border-neutral-200 text-center">
            <div className="text-xs text-neutral-500 mb-1">Term Sheets</div>
            <div className="text-lg font-semibold">{data.termSheetStats?.total || 0}</div>
          </div>
          <div className="bg-white p-3 rounded-lg border border-neutral-200 text-center">
            <div className="text-xs text-neutral-500 mb-1">Committed</div>
            <div className="text-lg font-semibold">{formatCurrency(data.termSheetStats?.totalCommitted || 0)}</div>
          </div>
        </div>
      );
    }

    // CREATE/UPDATE CONTACT
    if ((action === 'create_contact' || action === 'update_contact') && data.id) {
       return (
        <div className="mt-3 bg-white p-3 rounded-lg border border-neutral-200 shadow-sm flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center text-neutral-600 font-medium">
            {data.name?.[0]}
          </div>
          <div>
            <div className="font-medium text-sm">{data.name}</div>
            <div className="text-xs text-neutral-500">{data.title}</div>
            <div className="text-xs text-neutral-400">{data.email}</div>
          </div>
        </div>
       );
    }

    // Fallback for success message with simple data
    if (Object.keys(data).length > 0 && !Array.isArray(data)) {
        return (
             <div className="mt-2 text-xs font-mono bg-neutral-100 p-2 rounded overflow-x-auto text-neutral-600">
                {JSON.stringify(data, null, 2)}
             </div>
        )
    }

    return null;
  };

  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm whitespace-pre-wrap">{content}</p>
      {renderData()}
    </div>
  );
}
